import { useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDBA } from '@/contexts/DBAContext';
import { ActionBar, EstimateInputSection, FieldStaffShell, LeadAttachSection, MobileEstimatorHeader, PhotoSection, SetupModal, SummarySection } from '@/components/estimator/MobileEstimatorSections';
const FIELD_SETUP_KEY = 'fieldcrm-mobile-estimator-field-setup-v1';
const fieldSetupKeyFor = (dbaId) => `${FIELD_SETUP_KEY}:${dbaId || 'all-businesses'}`;
const readSessionJson = (key) => {
  try {
    const value = window.sessionStorage?.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    
    return null;
  }
};
const writeSessionJson = (key, value) => {
  try {
    window.sessionStorage?.setItem(key, JSON.stringify(value));
  } catch {
    return false;
  }
};

const initialForm = {
  load_category_key: 'full_pickup',
  disposal_profile_key: 'household_junk',
  estimated_hours: 2,
  workers: 2,
  round_trip_miles: 15,
  estimated_cubic_yards: 3,
  tire_counts: {},
  override_price: '',
  override_reason: '',
  selected_access_fee_keys: [],
  selected_specialty_items: [],
};

export default function MobileEstimatorPage() {
  const { user, logout } = useAuth();
  const { selectedDBA } = useDBA();
  const [bundle, setBundle] = useState(null);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [estimate, setEstimate] = useState(null);
  const [savedEstimate, setSavedEstimate] = useState(null);
  const [quote, setQuote] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupDraft, setSetupDraft] = useState(initialForm);

  const dbaParam = useMemo(() => selectedDBA ? { dba_id: selectedDBA } : {}, [selectedDBA]);
  const selectedCategory = bundle?.load_categories?.find((item) => item.key === form.load_category_key);
  const selectedDisposalProfile = bundle?.disposal_profiles?.find((item) => item.key === form.disposal_profile_key);
  const canQuote = !!estimate && !!selectedLead;
  const fieldSetupKey = useMemo(() => fieldSetupKeyFor(selectedDBA), [selectedDBA]);
  const backPath = user?.role === 'field_staff' ? '/staff' : '/estimator';

  useEffect(() => {
    const savedSetup = readSessionJson(fieldSetupKey) || readSessionJson(FIELD_SETUP_KEY);
    if (savedSetup) {
      setForm((prev) => ({ ...prev, ...savedSetup }));
      setSetupDraft((prev) => ({ ...prev, ...savedSetup }));
    }
    const load = async () => {
      setLoadError('');
      const [{ data: config }, { data: recentLeads }] = await Promise.all([
        api.get('/estimator/config', { params: dbaParam }),
        api.get('/estimator/leads', { params: dbaParam }),
      ]);
      setBundle(config);
      setLeads(recentLeads.slice(0, 20));
      if (recentLeads.length) setSelectedLead(recentLeads[0]);
      if (!savedSetup) setShowSetup(true);
    };
    load().catch((err) => {
      const msg = formatApiError(err.response?.data?.detail);
      setMessage(msg);
      setLoadError(msg);
    });
  }, [selectedDBA, fieldSetupKey, dbaParam]);

  const saveFieldSetup = () => {
    const nextSetup = {
      load_category_key: setupDraft.load_category_key || 'full_pickup',
      estimated_hours: Number(setupDraft.estimated_hours) || 2,
      workers: Number(setupDraft.workers) || 2,
      round_trip_miles: Number(setupDraft.round_trip_miles) || 15,
    };
    writeSessionJson(fieldSetupKey, nextSetup);
    setForm((prev) => ({ ...prev, ...nextSetup }));
    setEstimate(null);
    setSavedEstimate(null);
    setQuote(null);
    setShowSetup(false);
    setMessage('Field defaults saved.');
  };

  const skipFieldSetup = () => {
    writeSessionJson(fieldSetupKey, {
      load_category_key: initialForm.load_category_key,
      estimated_hours: initialForm.estimated_hours,
      workers: initialForm.workers,
      round_trip_miles: initialForm.round_trip_miles,
    });
    setShowSetup(false);
  };

  const searchLeads = async () => {
    const { data } = await api.get('/estimator/leads', { params: { ...dbaParam, search } });
    setLeads(data.slice(0, 20));
    if (data.length && !selectedLead) setSelectedLead(data[0]);
  };

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setEstimate(null);
    setSavedEstimate(null);
    setQuote(null);
  };

  const toggleAccess = (key) => {
    update('selected_access_fee_keys', form.selected_access_fee_keys.includes(key)
      ? form.selected_access_fee_keys.filter((item) => item !== key)
      : [...form.selected_access_fee_keys, key]);
  };

  const setSpecialtyQty = (key, quantity) => {
    const qty = Number(quantity) || 0;
    update('selected_specialty_items', qty > 0
      ? [...form.selected_specialty_items.filter((item) => item.key !== key), { key, quantity: qty }]
      : form.selected_specialty_items.filter((item) => item.key !== key));
  };

  const payload = () => ({
    ...form,
    mode: 'quick',
    dba_id: selectedLead?.dba_id || selectedDBA || '',
    lead_id: selectedLead?.id || '',
    rental_vehicle_needed: selectedCategory?.requires_uhaul || false,
  });

  const updateTireCount = (key, value) => update('tire_counts', { ...(form.tire_counts || {}), [key]: Number(value) || 0 });
  const selectLead = (leadId) => { setSelectedLead(leads.find((lead) => lead.id === leadId) || null); setSavedEstimate(null); setQuote(null); };
  const openSetup = () => { setSetupDraft((prev) => ({ ...prev, ...form })); setShowSetup(true); };

  const calculate = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { data } = await api.post('/estimator/calculate', payload());
      setEstimate(data.calculation);
      setSavedEstimate(null);
      setQuote(null);
      setMessage('Estimate calculated.');
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveAndQuote = async () => {
    if (!selectedLead) return setMessage('Select a lead before creating a quote.');
    if (!estimate) return setMessage('Calculate first, then create the quote.');
    setSaving(true);
    setMessage('');
    try {
      let estimateToQuote = savedEstimate;
      if (!estimateToQuote?.id) {
        const { data } = await api.post('/estimator/estimates', payload());
        estimateToQuote = data;
        setSavedEstimate(data);
      }
      const { data: quoteData } = await api.post(`/estimator/estimates/${estimateToQuote.id}/quote`);
      setQuote(quoteData);
      const smsStatus = quoteData.notification_result?.sms?.status || quoteData.sms_result?.status;
      const emailStatus = quoteData.notification_result?.email?.status || quoteData.email_result?.status;
      const delivery = smsStatus === 'skipped' ? 'Email queued/logged; SMS skipped.' : `Email ${emailStatus || 'logged'} · SMS ${smsStatus || 'logged'}.`;
      setMessage(`Quote ${quoteData.quote_number} created. ${delivery}`);
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveToLead = async () => {
    if (!selectedLead) return setMessage('Select a lead before saving the estimate.');
    if (!estimate) return setMessage('Calculate first, then save to the lead.');
    setSaving(true);
    setMessage('');
    try {
      if (savedEstimate?.id) {
        setMessage(`Estimate ${savedEstimate.estimate_number} is already saved to this lead.`);
        return;
      }
      const { data } = await api.post('/estimator/estimates', payload());
      setSavedEstimate(data);
      setEstimate(data.calculation);
      setQuote(null);
      setMessage(`Estimate ${data.estimate_number} saved to ${selectedLead.contact_name || 'this lead'}.`);
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEstimateIfNeeded = async () => {
    if (!selectedLead) throw new Error('Select a lead before attaching photos.');
    if (savedEstimate?.id) return savedEstimate;
    const { data } = await api.post('/estimator/estimates', payload());
    setSavedEstimate(data);
    setEstimate(data.calculation);
    return data;
  };

  const uploadEstimatePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingPhoto(true);
    setMessage('');
    try {
      const estimateForPhoto = await saveEstimateIfNeeded();
      const data = new FormData();
      data.append('file', file);
      const { data: photo } = await api.post('/photos/upload', data, {
        params: { entity_type: 'estimate', entity_id: estimateForPhoto.id, photo_stage: 'estimate' },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotos((prev) => [photo, ...prev]);
      setMessage('Photo attached to the estimate and lead.');
    } catch (err) {
      setMessage(err.message || formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!bundle) {
    return <div className="min-h-screen bg-[#eef7ff] p-5 text-blue-950" data-testid="mobile-estimator-loading">{loadError ? <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800" data-testid="mobile-estimator-load-error">Mobile estimator could not load: {loadError}</div> : <div className="rounded-3xl bg-white p-5 text-lg font-black shadow-sm">Loading mobile estimator...</div>}</div>;
  }

  return (
    <div className="min-h-screen bg-[#eef7ff] pb-32 text-zinc-950" data-testid="mobile-estimator-page">
      <MobileEstimatorHeader backPath={backPath} user={user} />
      <FieldStaffShell user={user} logout={logout} message={message}>
        <LeadAttachSection selectedLead={selectedLead} leads={leads} search={search} setSearch={setSearch} searchLeads={searchLeads} selectLead={selectLead} openSetup={openSetup} />
        <EstimateInputSection bundle={bundle} form={form} selectedDisposalProfile={selectedDisposalProfile} showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced} update={update} toggleAccess={toggleAccess} setSpecialtyQty={setSpecialtyQty} updateTireCount={updateTireCount} />
        <PhotoSection photos={photos} uploadingPhoto={uploadingPhoto} uploadEstimatePhoto={uploadEstimatePhoto} />
        <SummarySection estimate={estimate} quote={quote} />
      </FieldStaffShell>
      <ActionBar calculate={calculate} saveToLead={saveToLead} saveAndQuote={saveAndQuote} saving={saving} canQuote={canQuote} />
      <SetupModal showSetup={showSetup} bundle={bundle} setupDraft={setupDraft} setSetupDraft={setSetupDraft} skipFieldSetup={skipFieldSetup} saveFieldSetup={saveFieldSetup} />
    </div>
  );
}